import {
  PatientActivityLogPage,
  PatientExtMobAssistLog,
  PatientExtMobCpmLog,
  PatientGameModeLog,
  PatientManualModeLog,
  PatientsGetManyPatientSessionsResponse
} from '@bioliberty/lifemind';
import { getQueryCacheKey } from '../util';
import { formatUtcDatesAsLocal } from '../util/date';
import { downloadExportResponse } from './export-download';
import { tms } from './tms';

export type PatientActivityLog =
  | PatientExtMobCpmLog
  | PatientExtMobAssistLog
  | PatientManualModeLog
  | PatientGameModeLog;

interface GetPatientActivityLogsParams {
  patientId: string;
  page: number;
  pageSize: number;
  activityTypeIds?: string[];
  dateFrom?: string;
  dateTo?: string;
}

interface GetPatientSessionsParams {
  patientId: number;
  page: number;
  pageSize: number;
  activityTypeIds?: string[];
  dateFrom?: string;
  dateTo?: string;
}

export type GetPatientSessionsExportParams = {
  patientId: number;
  activityTypeIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  format?: string;
};

export const activityLogApi = tms.injectEndpoints({
  endpoints: (builder) => ({
    getPatientActivityLogs: builder.query<PatientActivityLogPage, GetPatientActivityLogsParams>({
      query: ({ patientId, page, pageSize, activityTypeIds, dateFrom, dateTo }) => ({
        url: `patients/${patientId}/activity-logs`,
        method: 'GET',
        params: {
          page: page,
          pageSize: pageSize,
          activityTypeIds: activityTypeIds?.join(',') || undefined,
          dateFrom,
          dateTo
        }
      }),
      providesTags: (_result, _error, params) => [{ type: 'PatientActivityLog', id: getQueryCacheKey(params) }]
    }),
    getPatientSessions: builder.query<PatientsGetManyPatientSessionsResponse, GetPatientSessionsParams>({
      query: ({ patientId, page, pageSize, activityTypeIds, dateFrom, dateTo }) => ({
        url: `patients/${patientId}/sessions`,
        method: 'GET',
        params: {
          page: page,
          pageSize: pageSize,
          activityTypeIds: activityTypeIds?.join(',') || undefined,
          dateFrom,
          dateTo
        }
      }),
      transformResponse: (response: PatientsGetManyPatientSessionsResponse) => ({
        ...response,
        items: response.items.map((session) => ({
          ...session,
          note: session.note ? formatUtcDatesAsLocal(session.note) : session.note
        }))
      }),
      providesTags: (_result, _error, params) => [{ type: 'PatientSessions', id: getQueryCacheKey(params) }]
    }),
    getPatientSessionsExport: builder.mutation<void, GetPatientSessionsExportParams>({
      query: ({ patientId, format, dateFrom, dateTo }) => ({
        url: `patients/${patientId}/sessions/export`,
        method: 'GET',
        params: { format, dateFrom, dateTo },
        responseHandler: downloadExportResponse,
        cache: 'no-store'
      })
    })
  }),
  overrideExisting: false
});

export const { useQuery: useGetPatientActivityLogsQuery } = activityLogApi.endpoints.getPatientActivityLogs;

export const { useQuery: useGetPatientSessionsQuery } = activityLogApi.endpoints.getPatientSessions;

export const { useMutation: useGetPatientSessionsExportQuery } = activityLogApi.endpoints.getPatientSessionsExport;
